import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import api from '../../../../Component/axios_Interceptor/api';
import { SERVER_ADDRESS } from '../../../../Component/server';
import { AdminActionButton } from '../../../../Component/UI/AdminPageHeader';

const endpoint = `${SERVER_ADDRESS}realtime/chat-forbidden-words`;

export default function LiveForbiddenWords({ onBusyChange }: { onBusyChange?: (busy: boolean) => void }) {
  const [words, setWords] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const version = useRef(0);
  const pending = useRef(false);

  const load = async () => {
    const request = ++version.current;
    setLoading(true); setLoaded(false); setError(''); setMessage('');
    try {
      const { data } = await api.get(endpoint);
      if (request !== version.current) return;
      if (!Array.isArray(data?.words) || data.words.some((word: unknown) => typeof word !== 'string')) throw new Error();
      setWords(data.words); setLoaded(true);
    } catch (failure: any) {
      if (request === version.current) setError(failure?.response?.data?.message || '금지어를 불러오지 못했습니다. 다시 불러오기를 눌러주세요.');
    } finally { if (request === version.current) setLoading(false); }
  };
  useEffect(() => { void load(); return () => { version.current++; }; }, []);
  const save = async (nextWords: string[]): Promise<boolean> => {
    if (pending.current || loading || !loaded) return false;
    if (nextWords.length > 200 || nextWords.some(word => word.length > 100)) {
      setError('금지어는 100자 이내로 최대 200개까지 저장할 수 있습니다.'); return false;
    }
    pending.current = true; onBusyChange?.(true); setSaving(true); setError(''); setMessage('');
    try {
      const { data } = await api.post(endpoint, { words: nextWords }, { headers: { 'Content-Type': 'application/json' } });
      if (!Array.isArray(data?.words) || data.words.some((word: unknown) => typeof word !== 'string')) throw new Error();
      setWords(data.words); setMessage('금지어가 저장되었습니다.'); return true;
    } catch (failure: any) {
      setError(failure?.response?.data?.message || '금지어를 저장하지 못했습니다. 다시 시도해주세요.');
      return false;
    } finally { pending.current = false; onBusyChange?.(false); setSaving(false); }
  };
  const add = async () => {
    const word = input.trim().normalize('NFKC');
    if (!word) return;
    if (words.some(existing => existing.toLocaleLowerCase('ko-KR') === word.toLocaleLowerCase('ko-KR'))) {
      setError('이미 등록된 금지어입니다.'); return;
    }
    const draft = input;
    if (await save([...words, word])) setInput(current => current === draft ? '' : current);
  };

  return <Form onSubmit={event => { event.preventDefault(); void add(); }}>
        <strong>금지어 목록</strong>
        <Tags aria-label="금지어 목록" aria-busy={loading || saving}>
          {words.map(word => <li key={word}><span>{word}</span>
            <button type="button" aria-label={`${word} 금지어 삭제`} disabled={loading || saving || !loaded}
              onClick={() => { void save(words.filter(existing => existing !== word)); }}>×</button>
          </li>)}
        </Tags>
        {loaded && !words.length && <p>등록된 금지어가 없습니다.</p>}
        <label htmlFor="live-forbidden-word-input">금지어 추가</label>
        <p id="live-forbidden-words-help">대소문자 구분 없이 포함된 단어를 확인합니다. 추가·삭제하면 바로 저장됩니다.</p>
        <InputRow>
          <input id="live-forbidden-word-input" aria-describedby="live-forbidden-words-help" maxLength={100}
            placeholder="금지어를 입력해주세요." value={input} disabled={loading || saving || !loaded}
            onKeyDown={event => { if (event.key === 'Enter' && event.nativeEvent.isComposing) event.preventDefault(); }}
            onChange={event => { setInput(event.target.value); setError(''); setMessage(''); }} />
          <AdminActionButton type="submit" $primary disabled={loading || saving || !loaded || !input.trim()}>추가</AdminActionButton>
        </InputRow>
        {loading && <p role="status">금지어를 불러오는 중입니다.</p>}
        {saving && <p role="status">저장 중…</p>}
        {error && <p role="alert">{error}</p>}
        {message && <p role="status">{message}</p>}
        <Actions>
          <AdminActionButton type="button" disabled={loading || saving} onClick={() => { void load(); }}>다시 불러오기</AdminActionButton>
        </Actions>
      </Form>
  ;
}

const Form = styled.form`
  display: flex; flex-direction: column; gap: var(--space-3);
  font-family: var(--font-family-base);
  label, > strong { font-size: var(--text-body); font-weight: 600; }
  p { margin: 0; color: var(--color-text-secondary); font-size: var(--text-caption); }
  p[role="alert"] { color: var(--color-text-danger); }
`;
const Tags = styled.ul`
  display: flex; flex-wrap: wrap; align-content: flex-start; gap: var(--space-2);
  min-height: var(--control-xl); max-height: 260px; overflow-y: auto; margin: 0; padding: var(--space-3);
  list-style: none; border: 1px solid var(--color-border-default); border-radius: var(--radius-md);
  li { display: inline-flex; align-items: center; gap: var(--space-1); max-width: 100%;
    padding: var(--space-1) var(--space-2); border-radius: var(--radius-pill); background: var(--color-brand-subtle); }
  span { min-width: 0; overflow-wrap: anywhere; color: var(--color-text-primary); font-size: var(--text-body); }
  button { flex-shrink: 0; width: var(--control-sm); min-height: var(--control-sm); border: 0;
    border-radius: var(--radius-pill); background: transparent; color: var(--color-text-secondary); font: inherit; cursor: pointer; }
  button:hover:not(:disabled) { background: var(--color-bg-muted); color: var(--color-text-primary); }
  button:disabled { color: var(--color-text-disabled); cursor: not-allowed; }
  button:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
  @media (pointer: coarse) { button { min-width: var(--touch-target); min-height: var(--touch-target); } }
`;
const InputRow = styled.div`
  display: flex; gap: var(--space-2);
  input { box-sizing: border-box; flex: 1; min-width: 0; min-height: var(--control-lg); padding: 0 var(--space-3);
    border: 1px solid var(--color-border-default); border-radius: var(--radius-md);
    background: var(--color-bg-surface); color: var(--color-text-primary); font: inherit; }
  input:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
  input:disabled { background: var(--color-bg-muted); color: var(--color-text-disabled); }
`;
const Actions = styled.div`display: flex; flex-wrap: wrap; justify-content: flex-end; gap: var(--space-2);`;
